import React, { useState, useRef, useEffect } from 'react';
import { Button, Table, Form, Select, Input, Space, Tag, Tabs, DatePicker, Row, Col, Tooltip } from 'antd';
import { useAntdTable, useSize, useDebounce } from 'ahooks';
import { useDispatch } from 'react-redux';
import { setBreadcrumbList } from '../../../store/menuInfo/menuInfo';
import apis from '../../../apis';
import AuditDetail from '../AuditDetail';
import './index.scss';

const { Option } = Select;

const IconView = (
    <svg t="1704265207410" viewBox="0 0 1024 1024" version="1.1" p-id="4261" width="12" height="12" style={{ cursor: 'pointer' }}>
        <path d="M896 64H128c-35.296 0-64 28.704-64 64v768c0 35.296 28.704 64 64 64h592a32 32 0 1 0 0-64H128V128h768v592a32 32 0 1 0 64 0V128c0-35.296-28.704-64-64-64z" p-id="4262"></path>
        <path d="M791.744 746.496A206.752 206.752 0 0 0 832 624c0-114.688-93.312-208-208-208S416 509.312 416 624s93.312 208 208 208a206.752 206.752 0 0 0 122.496-40.256l110.88 110.88a31.904 31.904 0 0 0 45.248 0 31.968 31.968 0 0 0 0-45.248l-110.88-110.88zM480 624c0-79.392 64.608-144 144-144s144 64.608 144 144-64.608 144-144 144-144-64.608-144-144zM800 264a32 32 0 0 0-32-32H256a32 32 0 0 0 0 64h512a32 32 0 0 0 32-32zM256 422.656a32 32 0 0 0 0 64h96a32 32 0 0 0 0-64H256z" p-id="4263"></path>
    </svg>
);

const IconAudit = (
    <svg t="1768375530687" className="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="7397" id="mx_n_1768375530689" width="16" height="16" style={{ cursor: 'pointer' }}>
        <path d="M184 818.1h657c19.2 0 37.1-6.9 50.5-19.4 13.6-12.6 21.1-29.6 21.1-47.8V626.7c0-36.4-32.8-67.2-71.6-67.2H656.4l-19.2-112.3c64.1-36.4 103.7-103.2 103.7-175.5 0-27.6-6-54.6-17.8-80.1-11.4-24.6-27.6-46.8-48.2-65.9-42.6-39.4-98.6-61.1-157.9-61.1-60.2 0-116.8 21.5-159.4 60.4-42.7 39.1-66.3 91.2-66.3 146.7 0 72.3 39.6 139 103.7 175.5l-19.2 112.3H184c-19.2 0-37.1 6.9-50.5 19.4-13.6 12.6-21.1 29.6-21.1 47.8v124.2c0 36.4 32.8 67.2 71.6 67.2z m7.9-180.1h192.2c35 0 65.5-26.7 69.7-60.8l24.7-123.9 0.1-0.4c4-27.7-9.8-53.5-35.9-67.4-42.9-22.8-68.5-64.7-68.5-112.1 0-72.9 64-132.1 142.7-132.1 78.7 0 142.7 58.5 142.7 130.4 0 47.4-25.6 89.3-68.5 112.1-25.5 13.6-40 41.3-35.9 69.1v0.2L580 577.2c4.2 34.1 34.8 60.8 69.8 60.8h183.4v103.4H191.9V638zM887.7 875.9H137.3c-22.1 0-40.2 18.1-40.2 40.2s18.1 40.2 40.2 40.2h750.4c22.1 0 40.2-18.1 40.2-40.2s-18-40.2-40.2-40.2z" p-id="7398"></path>
    </svg>
);

const AuditList = () => {
    const [viewState, setViewState] = useState('list'); // 'list' | 'view'
    const [currentAuditId, setCurrentAuditId] = useState(null);
    const [activeTab, setActiveTab] = useState('PROCESSED'); // 'PROCESSED' | 'MY_REQUEST'

    const refTable = useRef(null);
    const refHeader = useRef(null);
    const size = useSize(refTable);
    const headerSize = useSize(refHeader);
    const dispatch = useDispatch();

    useEffect(() => {
        if (viewState === 'list') {
            dispatch(setBreadcrumbList([{ title: '充值方案' }, { title: 'TOB方案审核' }]));
        }
    }, [viewState, dispatch]);

    const [form] = Form.useForm();

    const getTableData = ({ current, pageSize }, formData) => {
        const params = {
            page: current,
            page_size: pageSize,
            ...formData,
        };
        
        // Handle date ranges
        if (params.submit_time_start) {
             params.submit_time_start = params.submit_time_start.format('YYYY-MM-DD 00:00:00');
        }
        if (params.submit_time_end) {
             params.submit_time_end = params.submit_time_end.format('YYYY-MM-DD 23:59:59');
        }

        // Set parameters based on tab
        if (activeTab === 'PROCESSED') {
            params.audit_status = 'FINISHED';
        } else if (activeTab === 'MY_REQUEST') {
            params.only_mine = true;
        }

        Object.keys(params).forEach(key => {
            if (params[key] === undefined || params[key] === null || params[key] === '') {
                delete params[key];
            }
        });

        return apis.getTopupAuditList(params).then(res => {
            if (res.code === 200) {
                return {
                    total: res.data?.total_count || 0,
                    list: res.data?.data || []
                };
            }
            return {
                total: 0,
                list: []
            };
        });
    };

    const { tableProps, search, refresh } = useAntdTable(getTableData, {
        form,
        defaultPageSize: 10,
        manual: true,
    });

    const { submit, reset } = search;

    const topup_plan_name = Form.useWatch('topup_plan_name', form);
    const debounced_topup_plan_name = useDebounce(topup_plan_name, { wait: 500 });

    // Auto-submit when form values change
    useEffect(() => {
        submit();
    }, [
        activeTab,
        // List all form fields here to trigger search on change
        Form.useWatch('topup_plan_type_code', form),
        Form.useWatch('audit_result_code', form),
        Form.useWatch('topup_plan_status_code', form),
        debounced_topup_plan_name,
        Form.useWatch('submit_time_start', form),
        Form.useWatch('submit_time_end', form),
        Form.useWatch('audit_time_start', form),
        Form.useWatch('audit_time_end', form),
    ]);

    const handleView = (record) => {
        setCurrentAuditId(record.topup_plan_audit_id); 
        setViewState('view');
    };

    if (viewState === 'view') {
        return <AuditDetail 
            id={currentAuditId} 
            mode='view'
            onBack={() => {
                setViewState('list');
                refresh();
            }} 
        />;
    }

    const columns = [
        {
            title: '方案类别',
            dataIndex: 'topup_plan_type_name',
            align: 'center',
            render: (text) => text === 'ENTERPRISE_EDITION' ? '企业版' : (text === 'PERSONAL_EDITION' ? '个人版' : text),
        },
        {
            title: '方案编号',
            dataIndex: 'topup_plan_code',
            align: 'center',
        },
        {
            title: '方案名称',
            dataIndex: 'topup_plan_name',
            align: 'center',
        },
        {
            title: '权益点',
            dataIndex: 'equity_point',
            align: 'center',
        },
        {
            title: '原价 (元)',
            dataIndex: 'original_price',
            align: 'center',
            render: (text) => `¥${Number(text).toFixed(2)}`,
        },
        {
            title: '折后价 (元)',
            dataIndex: 'discount_price',
            align: 'center',
            render: (text) => text ? `¥${Number(text).toFixed(2)}` : '-',
        },
        {
            title: '提交审核版本',
            dataIndex: 'ver_no',
            align: 'center',
        },
        {
            title: '发布说明',
            dataIndex: 'publish_comment',
            align: 'center',
            render: (text) => text || '-',
        },
        {
            title: '商品状态',
            dataIndex: 'topup_plan_status_name',
            align: 'center',
            render: (text) => {
                const map = {
                    '已上架': { color: 'green' },
                    '未上架': { color: 'default' },
                    '已删除': { color: 'red' },
                };
                return <Tag color={map[text]?.color}>{text || '-'}</Tag>;
            }
        },
        {
            title: '上架版本',
            dataIndex: 'list_ver_no',
            align: 'center',
            render: (text) => text || '-',
        },
        {
            title: '提交人',
            dataIndex: 'submitter_name',
            align: 'center',
        },
        {
            title: '提交时间',
            dataIndex: 'submit_time',
            align: 'center',
        },
        {
            title: '审核人',
            dataIndex: 'auditor_name',
            align: 'center',
            render: (text) => text || '-',
        },
        {
            title: '审核时间',
            dataIndex: 'audit_time',
            align: 'center',
            render: (text) => text || '-',
        },
        {
            title: '审核结果',
            dataIndex: 'audit_result_code',
            align: 'center',
            render: (text) => {
                const map = {
                    'UNDER_REVIEW': { color: 'orange', text: '待审核' },
                    'AGREE': { color: 'green', text: '通过' },
                    'REJECT': { color: 'red', text: '驳回' },
                };
                return <Tag color={map[text]?.color}>{map[text]?.text || text}</Tag>;
            }
        },
        {
            title: '操作',
            key: 'action',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <div className="tableTagsBox">
                    <Tooltip title="查看">
                        <span onClick={() => handleView(record)} className="action-icon">
                            {IconView}
                        </span>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <div className="audit-list-container" ref={refTable}>
            <div className="filterItemsBox" ref={refHeader}>
                <Tabs 
                    activeKey={activeTab} 
                    onChange={setActiveTab}
                    items={[
                        { key: 'PROCESSED', label: '已审核' },
                        { key: 'MY_REQUEST', label: '我发起的' },
                    ]}
                    style={{ width: '100%', marginBottom: '8px' }}
                />
                
                <div className="filterItemsContent">
                    <Form form={form} component={false}>
                        <div className="filterItem">
                            <span className="filterItemSpan">方案类别</span>
                            <Form.Item name="topup_plan_type_code" noStyle>
                                <Select allowClear placeholder="全部" style={{ width: '180px' }}>
                                    <Option value="ENTERPRISE_EDITION">企业版</Option>
                                    <Option value="PERSONAL_EDITION">个人版</Option>
                                </Select>
                            </Form.Item>
                        </div>

                        {activeTab === 'PROCESSED' && (
                            <div className="filterItem">
                                <span className="filterItemSpan">审核结果</span>
                                <Form.Item name="audit_result_code" noStyle>
                                    <Select allowClear placeholder="全部" style={{ width: '180px' }}>
                                        <Option value="AGREE">通过</Option>
                                        <Option value="REJECT">驳回</Option>
                                        <Option value="UNDER_REVIEW">待审核</Option>
                                    </Select>
                                </Form.Item>
                            </div>
                        )}

                        <div className="filterItem">
                            <span className="filterItemSpan">方案状态</span>
                            <Form.Item name="topup_plan_status_code" noStyle>
                                <Select allowClear placeholder="全部" style={{ width: '180px' }}>
                                    <Option value="LISTED">已上架</Option>
                                    <Option value="NOT_LISTED">未上架</Option>
                                    <Option value="DELETED">已删除</Option>
                                </Select>
                            </Form.Item>
                        </div>

                        <div className="filterItem">
                            <span className="filterItemSpan">方案名称</span>
                            <Form.Item name="topup_plan_name" noStyle>
                                <Input placeholder="请输入方案名称" allowClear style={{ width: '180px' }} />
                            </Form.Item>
                        </div>

                        <div className="filterItem">
                            <span className="filterItemSpan">提交时间</span>
                            <Form.Item name="submit_time_start" noStyle>
                                <DatePicker 
                                    style={{ width: '120px' }} 
                                    placeholder="开始日期" 
                                    disabledDate={(current) => {
                                        const end = form.getFieldValue('submit_time_end');
                                        return end && current && current > end.endOf('day');
                                    }}
                                />
                            </Form.Item>
                            &nbsp;&nbsp;~&nbsp;&nbsp;
                            <Form.Item name="submit_time_end" noStyle>
                                <DatePicker 
                                    style={{ width: '120px' }} 
                                    placeholder="结束日期" 
                                    disabledDate={(current) => {
                                        const start = form.getFieldValue('submit_time_start');
                                        return start && current && current < start.startOf('day');
                                    }}
                                />
                            </Form.Item>
                        </div>
                    </Form>
                </div>
            </div>

            <div className="tableContent">
                <Table
                    columns={columns}
                    rowKey="topup_plan_audit_id"
                    {...tableProps}
                    className="myFormTable"
                    bordered={true}
                    scroll={{ x: 1300, y: size?.height ? size.height - (headerSize?.height || 0) - 180 + 'px' : undefined }}
                />
            </div>
        </div>
    );
};

export default AuditList;
